import React, { useEffect, useState } from 'react'
import { isNull, isUndefined } from 'lodash'
import { useLocation, useNavigate } from 'react-router-dom'
import userActionStore, {
  AddToCompareI,
  compareCardsDataI,
} from '../../datastore/UserActionStore'

import ApiManager from '../../api/ApiManager'
import CarouselHeader from '../../components/ComparePlansPage/CarouselHeader'
import { HttpMethods } from '../../enums/HttpMethodsEnum'
import LayoutComponent from '../../components/common/Layout/Layout.component'
import { MNSCompletePlan } from '../../interface/quotes/mNonSupplementPlans/MNSCompletePlan'
import { MSPlan } from '../../interface/quotes/mSupplementPlans/MSPlan'
import { MSPlanGenericDetails } from '../../interface/quotes/mSupplementPlans/MSPlanGenericDetails'
import MedicareAdvantageTable from '../../components/ComparePlansPage/Tables/MedicareAdvantageTable'
import MedicarePartDTable from '../../components/ComparePlansPage/Tables/MedicarePartDTable'
import { MedicareQuotesType } from '../../enums/MedicareQuoteTypeEnum'
import MedicareSupplementTable from '../../components/ComparePlansPage/Tables/MedicareSupplementTable'
import SecureComponent from '../common/SecureComponent'
import SkeletonCard from '../../components/DashboardPage/SkeletonCard'
import StringConstants from '../../constants/StringConstants'
import { Switch } from '@mui/material'
import Tooltip from '../../components/common/Tooltip/Tooltip.component'
import UrlConstants from '../../constants/UrlConstants'
import { getApiErrorMessage } from '../../utils/StringUtils'
import { observer } from 'mobx-react'
import snackbarStore from '../../datastore/SnackbarStore'
import styled from '@emotion/styled'
import theme from '../../global/theme'
import { updateCheckpoint } from '../../service/customer'

const Container = styled.div`
  padding-bottom: 50px;
  min-height: 100vh;
  background-color: ${theme.colors.white};
`

const CarouselHeaderWrapper = styled.div`
  overflow: hidden;
  position: relative;
`

const HeaderWrapper = styled.div`
  position: -webkit-sticky;
  position: sticky;
  background-color: white;
  top: 84px;
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);
  z-index: 1;
  @media screen and (max-width: 600px) {
    top: 80px;
    padding-top: 10px;
  }
`

const CompareDifferenceToggleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px 20px;

  h3 {
    font-size: 18px;
    color: ${theme.colors.primary};
  }

  p {
    .label {
      color: ${theme.colors.textGrey};
    }
  }
`

const ComparePlansPage = () => {
  const [cardDetailsInOrder, setCardDetailsInOrder] = React.useState<
    (MSPlan | undefined)[]
  >([])
  const [mnsCardDetailsInOrder, setMNSCardDetailsInOrder] =
    React.useState<MNSCompletePlan>()

  const [comparePlansData, setComparePlansData] = useState<AddToCompareI>(
    userActionStore.comparePlanCards
  )

  const [state, setState] = React.useState({
    loading: false,
  })

  const [medicarePlanType, setMedicarePlanType] = React.useState<string>(
    userActionStore.comparePlanCards.planType
  )

  const location = useLocation()

  const [compareDifferences, setCompareDifferences] = React.useState(false)
  const searchParams = new URLSearchParams(location.search)

  const targetScroll = (event: any) => {
    const selectedTarget = event.target
    const scrollLeft = selectedTarget.scrollLeft

    const targets: any = document.getElementsByClassName('scrollTarget')

    for (let i = 0; i < targets.length; i++) {
      if (targets[i] !== selectedTarget) {
        // Force layout recalculation and immediate update
        targets[i].style.scrollBehavior = 'auto'
        targets[i].scrollLeft = scrollLeft
      }
    }
  }

  React.useEffect(() => {
    // Get all 'id' values (can be multiple)
    const planIds = searchParams.getAll('id')

    // Get the 'type' value
    const type = searchParams.get('type')

    if (!isNull(type) && planIds.length > 0) {
      setState({
        ...state,
        loading: true,
      })
      if (type !== MedicareQuotesType.MEDICARE_SUPPLEMENT) {
        setMedicarePlanType(type)
        compareMNSPlans(planIds)
      } else {
        setMedicarePlanType(type)
        compareMSPlans(planIds)
      }
    }
  }, [location.search])

  React.useEffect(() => {
    const medicareQuoteType = userActionStore.comparePlanCards.planType
    let planIds: (string | number | undefined)[] = []

    if (medicareQuoteType !== MedicareQuotesType.MEDICARE_SUPPLEMENT) {
      planIds = userActionStore
        .getComparePlanCards()
        .compareCardsData.map((iterator) => iterator.sunfirePlanId)
    } else {
      planIds = userActionStore
        .getComparePlanCards()
        .compareCardsData.map((iterator) => iterator.id)
    }

    if (planIds.length > 0) {
      const validPlanIds = planIds.filter(
        (id): id is number => id !== undefined
      )

      const queryParams = validPlanIds
        .map((id) => `id=${encodeURIComponent(id.toString())}`)
        .join('&')

      const path = `${
        location.pathname
      }?${queryParams}&type=${encodeURIComponent(medicareQuoteType)}`

      updateCheckpoint({ path, action: 'COMPARED_PLAN' }).catch(() => {})
    }
  }, [])

  const compareMNSPlans = (planIds: any[]) => {
    ApiManager.makeApiCallWithAuthentication(
      UrlConstants.RETRIEVE_MNS_COMPARE_PLAN_DETAILS.USECASE,
      HttpMethods.POST_METHOD,
      {
        planIds,
      }
    )
      .then((response) => {
        const responseData = response.data.data
        if (!isUndefined(responseData)) {
          const cards = responseData.plans.map((medicareAdvantagePlan: any) => {
            return {
              id: medicareAdvantagePlan.id,
              name: medicareAdvantagePlan.name,
              premium: medicareAdvantagePlan.premium,
              carrier: medicareAdvantagePlan.carrierName,
              enrollRequestStatus: medicareAdvantagePlan.enrollRequested,
              planType: MedicareQuotesType.MEDICARE_NON_SUPPLEMENT,
              sunfirePlanId: medicareAdvantagePlan.sunfirePlanId,
              rating: medicareAdvantagePlan.fmtRating,
              moop: medicareAdvantagePlan.moop,
            }
          })

          setComparePlansData((prev) => ({
            ...prev,
            compareCardsData: cards,
          }))
          setMNSCardDetailsInOrder(responseData ? responseData : {})
        } else throw new Error('Response is undefined')
      })
      .catch((err) => {
        snackbarStore.set({
          snackbarMessage: getApiErrorMessage('fetch MNS plan details'),
          snackbarOpen: true,
          snackbarType: 'error',
        })
      })
      .finally(() => {
        setState({
          ...state,
          loading: false,
        })
      })
  }

  const compareMSPlans = (planIds: string[]) => {
    ApiManager.makeApiCallWithAuthentication(
      UrlConstants.RETRIEVE_COMPARE_PLAN_DETAILS.USECASE,
      HttpMethods.POST_METHOD,
      {
        medicareQuoteType: MedicareQuotesType.MEDICARE_SUPPLEMENT,
        forSpouse: false,
        planIds,
      }
    )
      .then((response) => {
        // Get plan details in the same order as userActionStore.comparePlanCards
        const responseData: {
          msPlan: MSPlan
          msPlanGenericDetails: MSPlanGenericDetails[]
        }[] = response.data.data

        if (!isUndefined(responseData)) {
          const cardsArray = planIds.map((iterator) => {
            const card = responseData.find((itr) => itr.msPlan.id === iterator)
            if (card) {
              // Only add if not already present
              const alreadyExists = comparePlansData.compareCardsData.some(
                (existingCard) => existingCard.id === card.msPlan.id
              )

              if (
                !alreadyExists &&
                comparePlansData.compareCardsData.length < planIds.length
              ) {
                const newCard: compareCardsDataI = {
                  id: card.msPlan.id,
                  name: `${card.msPlan.msCompanyBase.name} (Plan ${card.msPlan.planName})`,
                  premium: card.msPlan.mSupplementRate.month / 100,
                  carrier: card.msPlan.msCompanyBase.name,
                  enrollRequestStatus: card.msPlan.enrollRequested,
                  planType: MedicareQuotesType.MEDICARE_SUPPLEMENT,
                  medicareSupplementPlanType: card.msPlan.planName,
                  ridersApplied: card.msPlan.ridersApplied,
                  enrollmentRedirectURL: card.msPlan.enrollmentRedirectURL,
                  isEappEnabled: card.msPlan.isEappEnabled,
                  msEnrollmentApplicationKey:
                    card.msPlan.enrollmentApplicationKey,
                  msSpouseEnrollmentApplicationKey:
                    card.msPlan.spouseEnrollmentApplicationKey,
                }

                setComparePlansData((prev) => ({
                  ...prev,
                  compareCardsData: [...prev.compareCardsData, newCard],
                }))
              }

              // Update internal data
              card.msPlan.msPlanGenericDetails = card?.msPlanGenericDetails
            }

            return card?.msPlan
          })
          setCardDetailsInOrder(cardsArray ? cardsArray : [])
        } else throw new Error('Response is undefined')
      })
      .catch((err) => {
        snackbarStore.set({
          snackbarMessage: getApiErrorMessage('fetch plan details'),
          snackbarOpen: true,
          snackbarType: 'error',
        })
      })
      .finally(() => {
        setState({
          ...state,
          loading: false,
        })
      })
  }

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  })

  React.useEffect(() => {
    if (userActionStore.comparePlanCards.compareCardsData.length !== 0) {
      setState({
        ...state,
        loading: true,
      })
      const medicareQuoteType = userActionStore.comparePlanCards.planType
      if (medicareQuoteType !== MedicareQuotesType.MEDICARE_SUPPLEMENT) {
        const planIds = userActionStore
          .getComparePlanCards()
          .compareCardsData.map((iterator) => iterator.sunfirePlanId)
        if (planIds) compareMNSPlans(planIds)
      } else {
        const planIds = userActionStore
          .getComparePlanCards()
          .compareCardsData.map((iterator) => iterator.id)
        compareMSPlans(planIds)
      }
    }
  }, [userActionStore.comparePlanCards.compareCardsData])

  const navigate = useNavigate()

  const handleBack = () => {
    navigate(-1)
  }

  return (
    <LayoutComponent>
      <Container>
        <HeaderWrapper>
          <CarouselHeaderWrapper>
            <CarouselHeader
              syncScroll={targetScroll}
              className='scrollSource'
              handleBack={handleBack}
              loading={state.loading}
              cardDetailsInOrder={
                medicarePlanType === MedicareQuotesType.MEDICARE_SUPPLEMENT
                  ? cardDetailsInOrder
                  : mnsCardDetailsInOrder
              }
              planType={medicarePlanType}
              comparePlanCards={comparePlansData}
            />
          </CarouselHeaderWrapper>
          {state.loading && (
            <div style={{ padding: '20px' }}>
              <SkeletonCard quantity={20} />
            </div>
          )}
        </HeaderWrapper>

        <CompareDifferenceToggleWrapper>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <h3>Compare Differences</h3>{' '}
            <Tooltip
              title={StringConstants.TOOLTIP_COMPARE_DIFFERENCE}
              content={'?'}
            />
          </div>
          <p>
            <span className='label'>No</span>
            <Switch
              checked={compareDifferences}
              onChange={(e) => {
                setCompareDifferences(e.target.checked)
              }}
            />
            <span className='label'>Yes</span>
          </p>
        </CompareDifferenceToggleWrapper>

        {!state.loading &&
          medicarePlanType === MedicareQuotesType.MEDICARE_ADVANTAGE && (
            <MedicareAdvantageTable
              cardDetailsInOrder={mnsCardDetailsInOrder as MNSCompletePlan}
              compareDifferences={compareDifferences}
              syncScroll={targetScroll}
            />
          )}

        {!state.loading &&
          medicarePlanType === MedicareQuotesType.MEDICARE_SUPPLEMENT && (
            <MedicareSupplementTable
              compareDifferences={compareDifferences}
              cardDetailsInOrder={cardDetailsInOrder as (MSPlan | undefined)[]}
              syncScroll={targetScroll}
            />
          )}

        {!state.loading &&
          medicarePlanType === MedicareQuotesType.MEDICARE_PRESCRIPTION && (
            <MedicarePartDTable
              compareDifferences={compareDifferences}
              cardDetailsInOrder={mnsCardDetailsInOrder as MNSCompletePlan}
              syncScroll={targetScroll}
            />
          )}
      </Container>
    </LayoutComponent>
  )
}

export default SecureComponent(observer(ComparePlansPage))
